/**
 * Tracks which chat lines should flash the "new message" highlight (see
 * lib/freshChat). Returns a stable registry: `isFresh(id)` while a line hasn't
 * played its highlight yet, `markPlayed(id)` once it has — so each line lights
 * up exactly once, on first render, even as FlatList recycles rows.
 */
import { useCallback, useMemo, useRef } from 'react'
import type { ChatMessageResponse } from '@warcarp/api-types'
import { initialFreshIds, liveFreshIds } from '../lib/freshChat'

const VISUAL_FIXTURES = process.env.EXPO_PUBLIC_VISUAL_FIXTURES === '1'

export interface FreshChatRegistry {
  isFresh: (messageId: string) => boolean
  markPlayed: (messageId: string) => void
}

export function useFreshChatHighlights({
  gameId,
  messages,
  messagesReady,
  seenMessageId,
  viewerId,
}: {
  gameId: string
  messages: ChatMessageResponse[]
  messagesReady: boolean
  seenMessageId: string | null | undefined
  viewerId: string | undefined
}): FreshChatRegistry {
  // Highlighting is standard game behaviour. Only still-frame fixtures disable
  // the animation; retired device preferences must never suppress new lines.
  const enabled = !VISUAL_FIXTURES
  const fresh = useRef(new Set<string>())
  const known = useRef(new Set<string>())
  const initializedFor = useRef<string | null>(null)

  // Computed DURING render (not in an effect) so a newly arrived line is
  // already fresh on its very first frame — the row can mount straight into
  // its "new" entrance instead of flashing as a normal row first. The ref
  // updates are idempotent, so a repeated render pass is harmless.
  const changed = useMemo(() => {
    if (!messagesReady || seenMessageId === undefined || !viewerId) return 0
    if (initializedFor.current !== gameId) {
      initializedFor.current = gameId
      fresh.current = new Set(enabled ? initialFreshIds(messages, seenMessageId, viewerId) : [])
      known.current = new Set(messages.map((message) => message.id))
      return messages.length + 1
    }
    const incoming = messages.filter((message) => !known.current.has(message.id))
    if (incoming.length === 0) return 0
    for (const message of incoming) known.current.add(message.id)
    if (!enabled) return 0
    for (const id of liveFreshIds(incoming, viewerId)) fresh.current.add(id)
    return incoming.length
  }, [enabled, gameId, messages, messagesReady, seenMessageId, viewerId])

  const markPlayed = useCallback((messageId: string) => {
    fresh.current.delete(messageId)
  }, [])

  // A new registry identity whenever messages change, so rows re-check.
  return useMemo<FreshChatRegistry>(
    () => ({ isFresh: (messageId) => fresh.current.has(messageId), markPlayed }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-publish when the fresh set may have changed
    [markPlayed, changed, messages],
  )
}
